import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";

function flattenOptions(options, groups) {
  if (groups?.length) {
    return groups.flatMap((group) => group.options || []);
  }
  return options || [];
}

function SelectField({
  value,
  onChange,
  placeholder = "Select an option",
  options = [],
  groups = null,
  error = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });

  const flatOptions = useMemo(
    () => flattenOptions(options, groups),
    [options, groups],
  );
  const selected = flatOptions.find(
    (opt) => String(opt.value) === String(value),
  );

  const updateCoords = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    setCoords({
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updateCoords();
    const onReposition = () => updateCoords();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const renderOption = (opt) => {
    const isSelected = String(opt.value) === String(value);
    return (
      <button
        key={opt.value}
        type="button"
        disabled={opt.disabled}
        onClick={() => handleSelect(opt)}
        className={`w-full flex items-start gap-3 px-3.5 py-2.5 text-left transition-colors ${
          opt.disabled
            ? "opacity-40 cursor-not-allowed"
            : isSelected
              ? "bg-[#F8EEF8] text-brand-primary"
              : "text-slate-700 hover:bg-[#FBF6FB]"
        }`}
      >
        <span
          className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border ${
            isSelected
              ? "border-brand-primary bg-brand-primary text-white"
              : "border-slate-300 bg-white"
          }`}
        >
          {isSelected && <CheckIcon className="h-3 w-3" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-5">
            {opt.label}
          </span>
          {opt.hint && (
            <span className="block text-[11px] text-slate-400 mt-0.5 leading-4">
              {opt.hint}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`w-full h-[48px] px-4 flex items-center justify-between gap-3 rounded-xl border text-left transition-all duration-200 ${
          disabled
            ? "bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200"
            : error
              ? "bg-white border-red-400 ring-2 ring-red-100"
              : open
                ? "bg-white border-brand-primary ring-2 ring-brand-primary/20 shadow-[0_0_0_4px_rgba(152,37,152,0.06)]"
                : "bg-white border-slate-200 hover:border-brand-secondary"
        }`}
      >
        <span
          className={`truncate text-sm ${
            selected ? "text-slate-800 font-medium" : "text-slate-400"
          }`}
        >
          {selected?.label || placeholder}
        </span>
        <ChevronDownIcon
          className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180 text-brand-primary" : "text-slate-400"
          }`}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: coords.openUp ? "auto" : coords.top,
              bottom: coords.openUp ? coords.bottom : "auto",
              left: coords.left,
              width: coords.width,
              zIndex: 10050,
            }}
            className="seo-dropdown-menu bg-white rounded-2xl border border-slate-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="max-h-64 overflow-y-auto py-1.5">
              {groups?.length
                ? groups.map((group) => (
                    <div key={group.label} className="pb-1">
                      <div className="px-3.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-primary/70">
                        {group.label}
                      </div>
                      {(group.options || []).map(renderOption)}
                    </div>
                  ))
                : options.map(renderOption)}
              {flatOptions.length === 0 && (
                <div className="px-4 py-3 text-sm text-slate-400">
                  No options
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default SelectField;
